// src/pages/Teacher.jsx
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { BASE_URL } from "shared/lib/config";
import Header from "shared/ui/Header";

export default function Teacher() {
  const navigate = useNavigate();

  // auth/profile
  const [profile, setProfile] = useState(null);

  // UI + data
  const [status, setStatus] = useState("Loading…");
  const [classes, setClasses] = useState([]);
  const [rawPayload, setRawPayload] = useState(null);

  // helpers
  const getStore = () => {
    if (sessionStorage.getItem("idToken")) return sessionStorage;
    if (localStorage.getItem("idToken")) return localStorage;
    return localStorage; // default to local for caching
  };

  const hardLogout = () => {
    ["idToken", "userProfile"].forEach((k) => {
      sessionStorage.removeItem(k);
      localStorage.removeItem(k);
    });
    navigate("/");
  };

  // format util (academic year)
  const displayAY = (ay) => {
    if (!ay) return "—";
    if (typeof ay === "string") return ay;
    if (typeof ay === "object") return ay.name || ay._id || "—";
    return "—";
  };

  /**
   * Create (fallback mode, when API didn't send classes):
   *  - counts: Map<classId, Set<uniqueTestTakers>>
   *  - info:   Map<classId, { id, name, academicYear, slug }>
   */
  const indexByClassUniqueStudents = (assessments) => {
    const counts = new Map(); // classId -> Set<unique test_taker_name>
    const info = new Map();   // classId -> meta

    for (const a of assessments || []) {
      const clsObj = a?.classId && typeof a.classId === "object" ? a.classId : null;
      const classId = clsObj?._id || (typeof a?.classId === "string" ? a.classId : null);
      if (!classId) continue;

      // metadata
      if (!info.has(classId)) {
        info.set(classId, {
          id: classId,
          name: clsObj?.name || "—",
          academicYear: clsObj?.academicYear || null,
          slug: clsObj?.slug || null,
        });
      }

      // unique test takers by name (could swap to voucher if you prefer attempts)
      const taker = (a?.test_taker_name || "").trim();
      if (!counts.has(classId)) counts.set(classId, new Set());
      if (taker) counts.get(classId).add(taker);
    }

    return { counts, info };
  };

  /**
   * Build classes[] from either API-provided classes or from assessments (fallback).
   */
  const buildClasses = ({ apiClasses, assessments }) => {
    // If API provided classes with studentsCount, prefer those.
    if (Array.isArray(apiClasses) && apiClasses.length) {
      return apiClasses
        .map((c) => ({
          id: c.id || c._id,
          slug: c.slug || null,
          name: c.name || "—",
          academicYear: c.academicYear || null,
          studentsCount: c.studentsCount ?? 0,
        }))
        .sort((a, b) => String(a.name).localeCompare(String(b.name)));
    }

    // Fallback: compute from assessments
    const { counts, info } = indexByClassUniqueStudents(assessments);
    const result = [];
    for (const [classId, set] of counts.entries()) {
      const meta = info.get(classId);
      result.push({
        id: classId,
        name: meta?.name || "—",
        academicYear: meta?.academicYear || null,
        studentsCount: set.size,
        slug: meta?.slug || null,
      });
    }
    return result.sort((a, b) => String(a.name).localeCompare(String(b.name)));
  };

  /* ----- 1) Make sure we’re at the top, and hydrate UI from cache instantly ----- */
  useEffect(() => {
    window.scrollTo(0, 0);

    const store = getStore();
    const cachedProfile = JSON.parse(store?.getItem("userProfile") || "null");
    if (cachedProfile) setProfile(cachedProfile);

    const cachedClasses = JSON.parse(store?.getItem("teacherClassesCache") || "null");
    if (Array.isArray(cachedClasses) && cachedClasses.length) {
      setClasses(cachedClasses);
      setStatus(""); // no loading text if we can show cards immediately
    }
  }, []);

  /* ----- 2) Verify & fetch fresh data in the background (no UI flicker) ----- */
  useEffect(() => {
    const init = async () => {
      const store = getStore();
      const token = store?.getItem("idToken");
      if (!token || !store) return hardLogout();

      try {
        // Only show loading text if we didn’t have cached classes
        const hadCachedClasses =
          Array.isArray(JSON.parse(store.getItem("teacherClassesCache") || "null")) &&
          JSON.parse(store.getItem("teacherClassesCache") || "null").length > 0;
        if (!hadCachedClasses) setStatus("Memuat kelas…");

        // Verify token / refresh profile silently
        const meRes = await fetch(`${BASE_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!meRes.ok) throw new Error("unauthorized");
        const me = await meRes.json();
        store.setItem("userProfile", JSON.stringify(me));
        setProfile(me);

        if (me.role !== "teacher" && me.role !== "principal") {
          return navigate("/report", { replace: true });
        }

        // Fetch assessments/classes
        const aRes = await fetch(`${BASE_URL}/api/assessments`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await aRes.json();
        setRawPayload(payload);

        if (!aRes.ok) throw new Error(payload?.message || "Failed to fetch");

        const apiClasses = Array.isArray(payload?.classes) ? payload.classes : null;
        const assessments = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
          ? payload
          : [];

        const built = buildClasses({ apiClasses, assessments });
        setClasses(built);
        setStatus("");

        // Cache classes for instant next render
        store.setItem("teacherClassesCache", JSON.stringify(built));
      } catch (err) {
        console.error(err);
        setStatus("Failed to load");
        hardLogout();
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const useCardsBackground = classes.length > 0;

  // normalize shape so the Debug section continues to work
  const normalizedAssessments = Array.isArray(rawPayload?.data)
    ? rawPayload.data
    : Array.isArray(rawPayload)
    ? rawPayload
    : [];

  const groupedClasses = classes.reduce((acc, c) => {
    const year = displayAY(c.academicYear);
    if (!acc[year]) acc[year] = [];
    acc[year].push(c);
    return acc;
  }, {});

  const sortedYears = Object.keys(groupedClasses).sort((a, b) => {
    const yearA = parseInt(a.split("/")[0]) || 0;
    const yearB = parseInt(b.split("/")[0]) || 0;
    return yearB - yearA;
  });

  return (
    <div
      className={
        useCardsBackground
          ? "min-h-screen bg-[radial-gradient(1100px_500px_at_-15%_-10%,rgba(99,102,241,0.10),transparent),radial-gradient(1000px_500px_at_115%_10%,rgba(16,185,129,0.10),transparent)]"
          : "min-h-screen bg-white"
      }
    >
      {/* Header */}
      <Header profile={profile} onLogout={hardLogout} />

      {/* Main */}
      <main className="mx-auto max-w-6xl px-5 py-10">
        {/* Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-extrabold text-slate-900">Kelas</h1>
          <p className="text-slate-500 mt-2">
            Pilih sebuah kelas untuk melihat analitik dan performa siswa secara detail.
          </p>
        </div>

        {/* Status */}
        {status && <div className="text-slate-500 mb-6">{status}</div>}

        {/* Empty state */}
        {classes.length === 0 && !status && (
          <div className="mt-24 flex flex-col items-center text-center text-slate-500">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-3 shadow-inner">
              <svg
                viewBox="0 0 24 24"
                className="w-8 h-8"
                aria-hidden="true"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19a4 4 0 10-6 0m9-6a3 3 0 11-6 0 3 3 0 016 0zm-12 0a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <div className="font-semibold text-slate-700">Tidak ada kelas yang ditemukan</div>
            <div className="text-sm">
              Kelas akan muncul disini ketika sudah ada siswa yang mengerjakan asesmen dan terdaftar pada kelas Anda.
            </div>
          </div>
        )}

        {/* Cards grid - grouped by academic year */}
        {classes.length > 0 && (
          <div className="space-y-12">
            {sortedYears.map((year) => (
              <div key={year}>
                {/* Academic Year Header */}
                <div className="mb-6 flex items-center gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-gradient-to-br from-slate-100 to-slate-50 border border-slate-200">
                      <svg
                        viewBox="0 0 24 24"
                        className="w-5 h-5 text-slate-600"
                        aria-hidden="true"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M8 7V3m8 4V3M3 11h18M5 19h14a2 2 0 002-2v-8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">{year}</h2>
                      <p className="text-sm text-slate-500">
                        {groupedClasses[year].length}{" "}
                        {groupedClasses[year].length === 1 ? "Kelas" : "Kelas"}
                      </p>
                    </div>
                  </div>
                  <div className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent" />
                </div>

                {/* Classes Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {groupedClasses[year].map((c) => (
                    <button
                      key={c.id || c._id}
                      type="button"
                      className="group text-left bg-white rounded-2xl border border-slate-200 shadow-md hover:shadow-xl transition-all duration-200 p-6 md:p-7"
                      onClick={() =>
                        navigate(`/teacher/class/${c.slug || c.id || c._id}`, {
                          state: {
                            classInfo: {
                              id: c.id || c._id,
                              name: c.name,
                              academicYear: c.academicYear,
                              studentsCount: c.studentsCount,
                              slug: c.slug || null,
                            },
                            // Use authoritative class link on AssessmentResult
                            assessments: normalizedAssessments.filter(
                              (a) =>
                                (a?.classId?._id || a?.classId) === (c.id || c._id)
                            ),
                          },
                        })
                      }
                    >
                      {/* Card Top */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-md">
                            <svg
                              viewBox="0 0 24 24"
                              className="w-5 h-5"
                              aria-hidden="true"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M17 20h-2a2 2 0 01-2-2v-3h6v3a2 2 0 01-2 2zM7 20H5a2 2 0 01-2-2v-3h6v3a2 2 0 01-2 2zM3 10h18M7 10V6a5 5 0 1110 0v4"
                              />
                            </svg>
                          </div>
                          <div>
                            <div className="text-lg md:text-xl font-semibold text-slate-900">
                              {/^\s*kelas/i.test(c.name || "") ? c.name : `Kelas - ${c.name}`}
                            </div>
                          </div>
                        </div>

                        <div className="rounded-full bg-slate-100 text-slate-400 p-2 group-hover:bg-slate-200 transition">
                          <svg
                            viewBox="0 0 24 24"
                            className="w-4 h-4"
                            aria-hidden="true"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>

                      {/* Card Footer */}
                      <div className="mt-6 flex items-center text-sm text-slate-600">
                        <div className="flex items-center gap-2">
                          <svg
                            viewBox="0 0 24 24"
                            className="w-4 h-4"
                            aria-hidden="true"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M17 20h-2a2 2 0 01-2-2v-3h6v3a2 2 0 01-2 2zM7 20H5a2 2 0 01-2-2v-3h6v3a2 2 0 01-2 2zM3 10h18M7 10V6a5 5 0 1110 0v4"
                            />
                          </svg>
                          <span className="tabular-nums">
                            {c.studentsCount ?? 0} siswa
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Debug / raw JSON */}
        {/* <pre className="text-xs text-slate-500">{JSON.stringify(rawPayload, null, 2)}</pre> */}
      </main>

      {/* Floating Help Button */}
      <Link
        to={{ pathname: "/contact", search: "?from=teacher" }}
        state={{ from: "teacher" }}
        onClick={() => sessionStorage.setItem("fromTeacher", "1")}
        className="fixed bottom-10 right-10 z-50 flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-full shadow-lg hover:shadow-2xl hover:scale-[1.08] transition-all duration-300 ease-out group"
        title="Butuh Bantuan?"
      >
        <svg
          className="w-7 h-7 transition-transform duration-300 group-hover:rotate-12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 18h.01M8 10a4 4 0 118 0c0 2-2 3-2 3M12 22a10 10 0 100-20 10 10 0 000 20z"
          />
        </svg>
        <span className="absolute inset-0 rounded-full bg-blue-500 opacity-0 blur-md group-hover:opacity-30 transition-opacity duration-300" />
      </Link>
    </div>
  );
}
